import { SplitLayout } from '@components/layout';
import { useAppDispatch } from '@hooks/generic';
import CircularProgress from '@mui/material/CircularProgress/CircularProgress';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { Forbidden } from '@routes/Forbidden';
import { NotFound } from '@routes/NotFound';
import { Unauthenticated } from '@routes/Unauthenticated';
import { setError } from '@store/errorSlice';
import { getMatchCase, isApiResponseError } from '@utils/service';
import { DetailedHTMLProps, HTMLAttributes, PropsWithChildren } from 'react';
import { FormattedMessage } from 'react-intl';

const HttpStateWrapper = ({
    isLoading,
    error,
    children,
    ...props
}: PropsWithChildren<
    DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> & {
        isLoading: boolean;
        error?: FetchBaseQueryError | SerializedError;
    }
>) => {
    const dispatch = useAppDispatch();
    if (isLoading) {
        return <CircularProgress className="absolute top-2/4 right-2/4" />;
    } else if (error && isApiResponseError(error)) {
        switch (error.status) {
            case 401:
                dispatch(setError({ status: 401 }));
                return <Unauthenticated />;
            case 403:
                dispatch(setError({ status: 403, case: getMatchCase() }));
                return (
                    <SplitLayout>
                        <Forbidden />
                    </SplitLayout>
                );
            case 404:
                dispatch(setError({ status: 404 }));
                return <NotFound fullPage />;
            default:
                dispatch(setError({ status: error.status }));
                return (
                    <p>
                        <FormattedMessage id="error.generic" defaultMessage="An error has occurred" />
                    </p>
                );
        }
    } else {
        return children;
    }
};

export { HttpStateWrapper };
